/**
 * 简单音频播放器 - 支持流式PCM音频播放
 */
export class SimpleAudioPlayer {
  constructor(options = {}) {
    this.sampleRate = options.sampleRate || 16000;
    this.channels = options.channels || 1;
    this.volume = options.volume || 1;
    this.playbackRate = options.playbackRate || 1.0;
    this.fadeDuration = options.fadeDuration || 50;
    this.filterFrequency = options.filterFrequency || 8000;
    this.smallChunkThreshold = options.smallChunkThreshold || 5000;
    
    this.onStatusUpdate = options.onStatusUpdate || (() => {});
    this.onPlaybackComplete = options.onPlaybackComplete || (() => {});
    
    // 音频上下文和节点
    this.audioContext = null;
    this.gainNode = null;
    this.scriptProcessor = null;
    
    // 缓冲区管理
    this.continuousBuffer = new Float32Array(0);
    this.smallChunksBuffer = [];
    this.smallChunksSize = 0;
    this.lastChunkTime = 0;
    this.lastDataTime = 0;
    
    // 播放状态
    this.playbackPosition = 0;
    this.isPlaying = false;
    this.isPaused = true; // 初始化为暂停状态
    this.isCompleted = false;
    
    // 音频处理
    this.previousFrame = null;
    this.hasPreviousFrame = false;
    
    this.initAudioContext();
  }

  /**
   * 初始化音频上下文
   */
  initAudioContext() {
    try {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // 确保音频上下文处于运行状态
      if (this.audioContext.state === 'suspended') {
        this.audioContext.resume();
      }
      
      this.gainNode = this.audioContext.createGain();
      this.gainNode.gain.value = this.volume;
      
      // 创建脚本处理器
      const bufferSize = 4096;
      this.scriptProcessor = this.audioContext.createScriptProcessor(bufferSize, 0, 1);
      this.scriptProcessor.onaudioprocess = this.processAudio.bind(this);
      
      // 连接音频节点
      this.scriptProcessor.connect(this.gainNode);
      this.gainNode.connect(this.audioContext.destination);
      
      console.log('音频上下文初始化成功', this.audioContext.state);
    } catch (error) {
      console.error('音频上下文初始化失败:', error);
    }
  }

  /**
   * 播放Base64音频数据
   */
  async playBase64Audio(base64Data) {
    if (!base64Data) return;
    
    try {
      // 确保音频上下文处于运行状态
      if (this.audioContext && this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }
      
      const arrayBuffer = this.base64ToArrayBuffer(base64Data);
      const pcmData = this.decodePcmData(arrayBuffer);
      
      if (pcmData) {
        this.addPCMData(pcmData);
      }
    } catch (error) {
      console.error('播放Base64音频失败:', error);
    }
  }

  /**
   * Base64转ArrayBuffer
   */
  base64ToArrayBuffer(base64) {
    const binaryStr = atob(base64);
    const len = binaryStr.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryStr.charCodeAt(i);
    }
    return bytes.buffer;
  }

  /**
   * 解码PCM数据
   */
  decodePcmData(arrayBuffer) {
    try {
      const int16Array = new Int16Array(arrayBuffer);
      const floatArray = new Float32Array(int16Array.length);
      
      const compressionThreshold = 0.6;
      const compressionRatio = 0.7;
      
      for (let i = 0; i < int16Array.length; i++) {
        let sample = int16Array[i] / 32768.0;
        
        // 动态压缩处理
        if (Math.abs(sample) > compressionThreshold) {
          const excess = Math.abs(sample) - compressionThreshold;
          const compression = excess * (1 - compressionRatio);
          sample = sample > 0 
            ? compressionThreshold + compression
            : -compressionThreshold - compression;
        }
        
        floatArray[i] = sample * 0.85;
      }
      
      return floatArray;
    } catch (error) {
      console.error('解码PCM数据失败:', error);
      return null;
    }
  }

  /**
   * 添加PCM数据到缓冲区
   */
  addPCMData(pcmData) {
    const now = Date.now();
    this.lastDataTime = now;
    
    console.log('添加PCM数据', {
      dataLength: pcmData.length,
      bufferLength: this.continuousBuffer.length,
      isPaused: this.isPaused,
      isPlaying: this.isPlaying
    });
    
    // 直接添加到连续缓冲区，简化逻辑
    this.appendToContinuousBuffer(pcmData);
    
    // 不自动开始播放，等待手动触发
  }

  /**
   * 处理小片段缓冲
   */
  processSmallChunks() {
    if (this.smallChunksBuffer.length === 0) return;
    
    const totalLength = this.smallChunksBuffer.reduce((sum, chunk) => sum + chunk.length, 0);
    const mergedData = new Float32Array(totalLength);
    
    let offset = 0;
    for (const chunk of this.smallChunksBuffer) {
      mergedData.set(chunk, offset);
      offset += chunk.length;
    }
    
    this.appendToContinuousBuffer(mergedData);
    
    this.smallChunksBuffer = [];
    this.smallChunksSize = 0;
  }

  /**
   * 添加到连续缓冲区
   */
  appendToContinuousBuffer(pcmData) {
    const newBuffer = new Float32Array(this.continuousBuffer.length + pcmData.length);
    newBuffer.set(this.continuousBuffer);
    newBuffer.set(pcmData, this.continuousBuffer.length);
    this.continuousBuffer = newBuffer;
  }

  /**
   * 音频处理回调
   */
  processAudio(audioProcessingEvent) {
    const outputBuffer = audioProcessingEvent.outputBuffer;
    const outputData = outputBuffer.getChannelData(0);
    const bufferSize = outputData.length;
    
    // 如果暂停，输出静音
    if (this.isPaused) {
      for (let i = 0; i < bufferSize; i++) {
        outputData[i] = 0;
      }
      return;
    }
    
    // 检查播放完成条件
    const now = Date.now();
    const noNewDataTimeout = 3000;
    const hasReceivedData = this.continuousBuffer.length > 0;
    const isPlaybackComplete = hasReceivedData && 
      this.playbackPosition >= this.continuousBuffer.length && 
      now - this.lastDataTime > noNewDataTimeout;
    
    if (isPlaybackComplete && !this.isCompleted) {
      this.isCompleted = true;
      this.onStatusUpdate("播放完成");
      this.onPlaybackComplete();
      return;
    }
    
    // 重采样和音频处理
    const resampleRatio = this.sampleRate / this.audioContext.sampleRate / this.playbackRate;
    
    for (let i = 0; i < bufferSize; i++) {
      const srcIndex = this.playbackPosition + i * resampleRatio;
      const srcIndexInt = Math.floor(srcIndex);
      const fraction = srcIndex - srcIndexInt;
      
      let sample = 0;
      if (srcIndexInt < this.continuousBuffer.length) {
        // 线性插值
        sample = this.continuousBuffer[srcIndexInt] * (1 - fraction);
        if (srcIndexInt + 1 < this.continuousBuffer.length) {
          sample += this.continuousBuffer[srcIndexInt + 1] * fraction;
        }
        
        // 平滑处理
        if (this.hasPreviousFrame) {
          const frameIndex = i % this.previousFrame.length;
          const smoothFactor = 0.1;
          sample = sample * (1 - smoothFactor) + 
                   this.previousFrame[frameIndex] * smoothFactor;
          this.previousFrame[frameIndex] = sample;
        }
        
        // 淡入淡出处理
        const fadeSamples = Math.floor((this.fadeDuration * this.audioContext.sampleRate) / 1000);
        if (this.playbackPosition < fadeSamples) {
          const fadeInFactor = this.playbackPosition / fadeSamples;
          sample *= fadeInFactor;
        }
      }
      
      outputData[i] = sample * 0.95;
    }
    
    // 只有在非暂停状态下才推进播放位置
    if (!this.isPaused) {
      this.playbackPosition += bufferSize * resampleRatio;
    }
    
    // 初始化前一帧数据
    if (!this.hasPreviousFrame) {
      this.previousFrame = new Float32Array(bufferSize);
      this.previousFrame.set(outputData);
      this.hasPreviousFrame = true;
    }
  }

  /**
   * 暂停播放
   */
  pause() {
    this.isPaused = true;
    this.isPlaying = false;
    this.onStatusUpdate("已暂停");
  }

  /**
   * 恢复播放
   */
  resume() {
    if (this.audioContext && this.audioContext.state === 'suspended') {
      this.audioContext.resume();
    }
    
    // 只有在有音频数据时才开始播放
    if (this.continuousBuffer.length > 0) {
      this.isPaused = false;
      this.isPlaying = true;
      
      console.log('恢复播放', {
        bufferLength: this.continuousBuffer.length,
        playbackPosition: this.playbackPosition,
        isPaused: this.isPaused,
        isPlaying: this.isPlaying
      });
      
      this.onStatusUpdate("正在播放");
    }
  }

  /**
   * 重播
   */
  replay() {
    this.playbackPosition = 0;
    this.isCompleted = false;
    this.resume();
  }

  /**
   * 停止播放
   */
  stop() {
    this.pause();
    this.playbackPosition = 0;
    this.continuousBuffer = new Float32Array(0);
    this.smallChunksBuffer = [];
    this.smallChunksSize = 0;
    this.isCompleted = false;
  }

  /**
   * 获取播放状态
   */
  getStatus() {
    return {
      isPlaying: this.isPlaying,
      isPaused: this.isPaused,
      isCompleted: this.isCompleted,
      bufferLength: this.continuousBuffer.length,
      playbackPosition: this.playbackPosition
    };
  }

  /**
   * 检查是否有音频数据
   */
  hasAudioData() {
    return this.continuousBuffer.length > 0;
  }

  /**
   * 设置音量
   */
  setVolume(volume) {
    this.volume = Math.max(0, Math.min(1, volume));
    if (this.gainNode && !this.isPaused) {
      this.gainNode.gain.value = this.volume;
    }
  }
}